/**
 * FilterChip — the rounded toggle chip of the game's filter bars: the log
 * filters (GameLogFilterPanel) and the map's players filter
 * (PlayerFilterBar). Olive fill and rule while on, paper with a faint rule
 * while off; optional colour dots before the label.
 */
import React from 'react'
import { Text, TouchableOpacity, View } from 'react-native'
import type { TeamShapeName } from '@warcarp/api-types'
import { TeamShape } from './TeamShape'

export function FilterChip({
  label,
  active,
  onPress,
  dotColor,
  dotColors,
  labelColor,
  dotSize = 8,
  dotShape,
  activeRingColor,
  accessibilityLabel,
  testID,
}: {
  label: string
  active: boolean
  onPress: () => void
  dotColor?: string
  /** Several dots (a team's members), drawn overlapping. */
  dotColors?: readonly string[]
  /** The label's colour (a player's name in their colour); ink by default. */
  labelColor?: string
  /** Dot diameter (the map's players filter uses bigger dots). */
  dotSize?: number
  /** Draw each dot as this team shape instead of a circle. */
  dotShape?: TeamShapeName
  /** While on, a ring in this colour round the chip (the players filter). */
  activeRingColor?: string
  accessibilityLabel?: string
  testID?: string
}) {
  const dots = dotColors ?? (dotColor != null ? [dotColor] : [])
  return (
    <TouchableOpacity
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel ?? label}
      accessibilityState={{ selected: active }}
      aria-pressed={active}
      testID={testID}
      className={`flex-row items-center gap-1 px-2.5 py-1 rounded-full border ${
        active ? 'bg-olive/30 border-olive' : 'bg-paper border-olive/40'
      }`}
      // The ring is a 2px border; its extra pixel comes out of the padding, so
      // the chip (and the bar) keep their size.
      style={{ marginRight: 6, marginBottom: 4, ...(active && activeRingColor ? { borderWidth: 2, borderColor: activeRingColor, paddingHorizontal: 9, paddingVertical: 3 } : null) }}
    >
      {dots.length > 0 && (
        <View style={{ flexDirection: 'row' }}>
          {dots.map((color, i) => dotShape ? (
            <TeamShape key={i} shape={dotShape} size={dotSize} fill={color} style={{ marginLeft: i ? 2 : 0 }} />
          ) : (
            <View key={i} style={{ width: dotSize, height: dotSize, borderRadius: dotSize / 2, backgroundColor: color, marginLeft: i ? -Math.round(dotSize / 4) : 0 }} />
          ))}
        </View>
      )}
      <Text
        className={`font-typed text-xs ${labelColor ? '' : active ? 'text-ink' : 'text-ink-soft'}`}
        style={labelColor ? { color: labelColor, fontWeight: active ? '700' : '400' } : undefined}
      >
        {label}
      </Text>
    </TouchableOpacity>
  )
}
